import React, { useState } from "react";
import { message, Modal } from "antd";
import { Button, Heading, Text, Box, Checkbox, Flex } from "@chakra-ui/react";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { InputField, TextareaInput } from "components/common";
import { useDispatch } from "react-redux";
import { postLoanAction } from "redux/actions/loanActions";
import { formatNumberAmount } from "helpers/utilFunctions";
import VeendText from "components/common/Typography/Text";
import axios from "axios";

interface IRepaymentModalProps {
  showModal: boolean;
  loanRecord: any;
  setShowModal: (bool: boolean) => void;
  afterClose?: () => void;
}

interface RepaymentFormValues {
  amount: string;
  comment: string;
  isDirectDebit: boolean;
}

export default function RepaymentModal({
  showModal,
  setShowModal,
  loanRecord,
  afterClose,
}: IRepaymentModalProps) {
  const dispatch = useDispatch();
  const handleCancel = () => {
    setShowModal(false);
  };
  const [isBusy, setIsBusy] = useState(false);
  const lenderNumber = loanRecord?.user?.phoneNumber;
  const repayment = loanRecord?.repaymentAmount;
  const totalOutstanding = loanRecord?.totalOutstanding;
  const loanId = loanRecord?.loanId;
  const _id = loanRecord?._id;
  const tenure = loanRecord?.tenure;
  const clientId = loanRecord?.user?.clientId;
  const xJwt = localStorage.getItem("x_jwt");

  const hasMandateRef = !!loanRecord?.user?.paystackDirectDebitReference;

  const initializeCharge = async ({ amount }: { amount: string }) => {
    try {
      let headers = {
        "x-jwt": xJwt,
        "x-tag": localStorage.getItem("lenderTag"),
      };
      const baseUrl = process.env.REACT_APP_BASE_URL;
      setIsBusy(true);
      const res = await axios.post(
        `${baseUrl}/paystack/direct-debit/charge`,
        { loanId, amount },
        { headers: headers }
      );
      setIsBusy(false);
      return res.data;
    } catch (error) {
      message.error(error?.response?.data?.message || "An error occured");
      console.log(error);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal
      visible={showModal}
      onCancel={handleCancel}
      footer={null}
      width="50%"
      className="loan-config-modal"
      afterClose={afterClose}
    >
      <Heading as="h2" mb="3" fontSize={{ base: "xs", md: "sm", lg: "md" }}>
        Make Repayment
      </Heading>
      <Text
        fontSize={{ base: "xx-small", md: "xs" }}
      >{`Borrower number - ${lenderNumber}`}</Text>
      <Text
        fontSize={{ base: "xx-small", md: "xs" }}
      >{`Next repayment - ${formatNumberAmount(repayment)}`}</Text>
      <Text fontSize={{ base: "xx-small", md: "xs" }}>{`Tenure - ${tenure} ${
        tenure > 1 ? "Months" : "Month"
      }`}</Text>
      <Text
        fontSize={{ base: "xx-small", md: "xs" }}
      >{`Total outstanding - ${formatNumberAmount(totalOutstanding)}`}</Text>
      <Text
        fontSize={{ base: "xx-small", md: "xs" }}
      >{`Loan Id - ${loanId}`}</Text>
      <Formik
        initialValues={{
          amount: "",
          comment: "",
          isDirectDebit: false,
        }}
        onSubmit={async (values: RepaymentFormValues, { resetForm }) => {
          let repaymentActionData = {
            action: "makeRepayment",
            clientId: clientId,
            loanId: loanId,
            amount: values.amount,
            note: values.comment,
          };
          if (values.isDirectDebit) {
            const charge = await initializeCharge({ amount: values.amount });
            if (charge && charge.status === "success") {
              message.success(charge.data || "Charge initiated successfully");
              setShowModal(false);
              resetForm();
            }
          } else {
            dispatch(postLoanAction(xJwt, repaymentActionData, _id, dispatch));
            setShowModal(false);
            resetForm();
          }
        }}
        validationSchema={yup.object().shape({
          amount: yup
            .number()
            .typeError("Please enter a valid number")
            .required("Please enter amount you want to repay")
            .max(
              totalOutstanding,
              `Maximum repayment on this loan is ${totalOutstanding}`
            )
            .min(1),
          comment: yup
            .string()
            .required("Please provide a comment to process repayment"),
        })}
      >
        {({ values, handleChange, handleBlur, setFieldValue, errors }) => (
          <Box as={Form} textAlign="center">
            <InputField
              inputType="tel"
              name="amount"
              hasLabel
              label="Repayment Amount"
              placeholder="Enter Repayment Amount"
              value={values.amount}
              onBlur={() => handleBlur}
              onChange={handleChange}
              error={errors.amount}
              required
            />
            <TextareaInput
              name="comment"
              placeholder="Add Comment Here"
              hasLabel
              label="Additional Comment"
              value={values.comment}
              onBlur={() => handleBlur}
              onChange={handleChange}
              error={errors.comment}
              required
            />
            {hasMandateRef && (
              <Flex w="100%" alignItems="center" gap={2} py={4}>
                <Checkbox
                  value={String(values.isDirectDebit)}
                  onChange={evt => {
                    setFieldValue("isDirectDebit", evt.target.checked);
                  }}
                />
                <VeendText>Charge Mandate via Paystack</VeendText>
              </Flex>
            )}

            <Button type="submit" mt="3" isLoading={isBusy}>
              {values.isDirectDebit ? "Initiate Charge" : "Repay"}
            </Button>
          </Box>
        )}
      </Formik>
    </Modal>
  );
}
